import React, { ReactElement, useEffect, useState } from "react";
import portraits, {
  defaultPortrait,
  portraitsAltText,
  unlockedPortraits,
} from "../assets";
import { codeForSecretIcon, unlockedSecretIcons } from "../secretIcons";
import { SendWSCommand, WSCommandType } from "../types";
import PhotoCapture from "./PhotoCapture";
import { PHOTO_ICON_PATTERN } from "../photoIcons";
import "./Lobby.css";

type IconDrawerProps = {
  open: boolean;
  user: string;
  isHost: boolean;
  players: string[];
  playerToIcon: Record<string, string>;
  sendWSCommand: SendWSCommand;
  onClose: () => void;
};

/**
 * The look picker as a drawer on the right of the lobby: a preview of your seat card, the looks you
 * can use. Picking a look applies it right away (the server
 * keeps looks unique); Save closes the drawer once you have one.
 */
export default function IconDrawer(props: IconDrawerProps): ReactElement | null {
  const current = props.playerToIcon[props.user];
  const [capturing, setCapturing] = useState(false);
  const hasPhoto = current !== undefined && PHOTO_ICON_PATTERN.test(current);
  const hasLook = current !== undefined && current !== defaultPortrait;

  // Close on Escape, but only once a look has been chosen (the server needs one to start).
  useEffect(() => {
    if (!props.open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && hasLook) props.onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [props, hasLook]);

  if (!props.open) {
    return null;
  }

  const inUseByOthers = (id: string) =>
    props.players.some((p) => p !== props.user && props.playerToIcon[p] === id);

  const pick = (id: string) => {
    if (inUseByOthers(id) || id === current) return;
    props.sendWSCommand({
      command: WSCommandType.SELECT_ICON,
      icon: id,
      code: codeForSecretIcon(id),
    });
  };

  const usePhoto = (base64Jpeg: string) => {
    props.sendWSCommand({ command: WSCommandType.UPLOAD_PHOTO, photo: base64Jpeg });
    setCapturing(false);
  };

  return (
    <div className="id-layer">
      {capturing && <PhotoCapture onUse={usePhoto} onClose={() => setCapturing(false)} />}
      <div className="id-scrim" onClick={hasLook ? props.onClose : undefined} />
      <aside className="id-drawer" role="dialog" aria-modal="true" aria-label="Choose your look">
        <div className="id-head">
          <h2 className="id-title">Your look</h2>
          <button
            className="id-close"
            aria-label="Close"
            onClick={props.onClose}
            disabled={!hasLook}
          >
            ×
          </button>
        </div>

        <div className="id-preview">
          {hasLook ? (
            <img src={portraits[current]} alt="" className="id-preview-img" />
          ) : (
            <div className="id-preview-img id-preview-empty">?</div>
          )}
          <div className="id-preview-text">
            <div className="id-preview-name">{props.user}</div>
            {props.isHost && <span className="lb-tag lb-tag-host">Host</span>}
            <div className="id-hint">
              {hasLook ? "Preview of your seat card" : "Pick a look to take your seat"}
            </div>
          </div>
        </div>

        <div className="id-grid">
          <button
            className={"id-tile id-photo" + (hasPhoto ? " selected" : "")}
            aria-label={hasPhoto ? "Retake your photo" : "Take a photo with your camera"}
            onClick={() => setCapturing(true)}
          >
            {hasPhoto && <img src={portraits[current]} alt="" draggable={false} />}
            <span className="id-photo-label">
              <span className="id-photo-icon" aria-hidden="true">
                📷
              </span>
              {hasPhoto ? "Retake" : "Take photo"}
            </span>
          </button>
          {[...unlockedSecretIcons(), ...unlockedPortraits].map((id) => {
            if (!portraits[id]) return null;
            const taken = inUseByOthers(id);
            const selected = id === current;
            return (
              <button
                key={id}
                className={"id-tile" + (selected ? " selected" : "") + (taken ? " unavailable" : "")}
                aria-label={portraitsAltText[id] + (taken ? " (taken)" : "")}
                aria-pressed={selected}
                disabled={taken}
                onClick={() => pick(id)}
              >
                <img src={portraits[id]} alt="" draggable={false} />
                {taken && <span className="id-badge">Taken</span>}
              </button>
            );
          })}
        </div>

        <div className="id-spacer" />
        <button className="gold-button gold-button-large id-save" onClick={props.onClose} disabled={!hasLook}>
          <span className="gold-label">SAVE LOOK</span>
        </button>
      </aside>
    </div>
  );
}
